import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router';
import { installGlobalErrorReporting } from './api/clientLog.ts';
import { isApiError } from './api/http.ts';
import { RequireLogin } from './auth/RequireLogin.tsx';
import { ME_KEY } from './auth/session.ts';
import { AppErrorBoundary, PageLoading, RouteErrorScreen } from './components/ErrorScreens.tsx';
import './styles.css';

// When the session ends while a page is open (expiry, logout elsewhere), re-check who is
// logged in; RequireLogin then sends the learner to the login page. Not for the "who is
// logged in" query itself: that would refetch it forever (it never stops failing).
const recheckLogin = (error: unknown) => {
  if (isApiError(error, 'not_logged_in')) void queryClient.invalidateQueries({ queryKey: ME_KEY });
};
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.queryKey[0] !== ME_KEY[0]) recheckLogin(error);
    },
  }),
  mutationCache: new MutationCache({ onError: recheckLogin }),
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

// Each page is its own chunk, loaded when first visited (the editor and lesson code are not
// downloaded for pages that do not need them).
const router = createBrowserRouter([
  {
    path: '/',
    Component: Outlet,
    // Catches render errors in any page and unknown URLs (404), instead of a blank screen.
    ErrorBoundary: RouteErrorScreen,
    HydrateFallback: PageLoading,
    children: [
      {
        path: 'login',
        lazy: async () => ({ Component: (await import('./pages/LoginPage.tsx')).LoginPage }),
      },
      {
        path: 'admin/login',
        lazy: async () => {
          const { LoginPage } = await import('./pages/LoginPage.tsx');
          return { Component: () => <LoginPage admin /> };
        },
      },
      // Public: terms, privacy policy and licenses (drafts, design.md §9.5).
      {
        path: 'terms',
        lazy: async () => ({
          Component: (await import('./pages/legal/LegalPages.tsx')).TermsPage,
        }),
      },
      {
        path: 'privacy',
        lazy: async () => ({
          Component: (await import('./pages/legal/LegalPages.tsx')).PrivacyPage,
        }),
      },
      {
        path: 'licenses',
        lazy: async () => ({
          Component: (await import('./pages/legal/LegalPages.tsx')).LicensesPage,
        }),
      },
      {
        // Everything else needs a session (accounts are issued to RareTECH students only).
        Component: RequireLogin,
        children: [
          { index: true, element: <Navigate to="/courses/python-basic" replace /> },
          {
            path: 'password',
            lazy: async () => ({
              Component: (await import('./pages/PasswordPage.tsx')).PasswordPage,
            }),
          },
          {
            path: 'courses/:courseId',
            lazy: async () => ({ Component: (await import('./pages/CoursePage.tsx')).CoursePage }),
          },
          {
            path: 'lessons/:courseId/:lessonId',
            lazy: async () => ({ Component: (await import('./pages/LessonPage.tsx')).LessonPage }),
          },
          {
            path: 'playground',
            lazy: async () => ({ Component: (await import('./pages/Playground.tsx')).Playground }),
          },
          {
            path: 'playground/guide',
            lazy: async () => ({
              Component: (await import('./pages/PlaygroundGuide.tsx')).PlaygroundGuide,
            }),
          },
        ],
      },
      {
        // CS staff (ADR-0009): learner accounts only.
        path: 'admin/accounts',
        element: <RequireLogin staff />,
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import('./pages/admin/AccountsPage.tsx')).AccountsPage,
            }),
          },
        ],
      },
      {
        path: 'admin',
        element: <RequireLogin admin />,
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminPages.tsx')).AdminLearnersPage,
            }),
          },
          {
            path: 'learners/:loginId',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminPages.tsx')).AdminLearnerPage,
            }),
          },
        ],
      },
    ],
  },
]);

installGlobalErrorReporting();

const root = document.getElementById('root');
if (root === null) throw new Error('#root is missing');
createRoot(root).render(
  <StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </AppErrorBoundary>
  </StrictMode>,
);
